import { useFormikContext } from "formik";
import { useState } from "react";
import { Link } from "react-router-dom";
import { AutoComplete } from "@/core/components/ui/autocomplete";
import { MultiSelect } from "@/core/components/ui/multi-select";
import { Typography } from "@/core/components/ui/typography";
import type {
  PayrollRosterFormValues,
  PayrollTeam,
} from "@/payroll/roster/types";
import { useRosterUsers } from "@/payroll/roster/use-roster-users";
import { rosterTeamSnapshots } from "@/payroll/teams/utils";
import { DrawerField } from "./drawer-field";

interface RosterMemberAccessFieldsProps {
  businessId: string;
  teams: PayrollTeam[];
  saving: boolean;
}

export const RosterMemberAccessFields = ({
  businessId,
  teams,
  saving,
}: RosterMemberAccessFieldsProps) => {
  const { values: member, setValues } =
    useFormikContext<PayrollRosterFormValues>();
  const { query: usersQuery, userOptions } = useRosterUsers(businessId);
  const [userSearch, setUserSearch] = useState("");
  return (
    <>
      <DrawerField label="Employee profile">
        <AutoComplete
          ariaLabel="Employee profile"
          selectedValue={member.user_id ?? ""}
          searchValue={userSearch}
          onSearchValueChange={setUserSearch}
          items={userOptions}
          onSelectedValueChange={(id) =>
            void setValues((current) => ({ ...current, user_id: id || null }))
          }
          isLoading={usersQuery.isLoading}
          disabled={saving || usersQuery.isError}
          fullWidth
          placeholder="Choose an employee"
        />
        <Typography variant="bodyW3" textColor="secondary">
          Choose the person who should receive this worker’s pay statements.
        </Typography>
        {usersQuery.isError && (
          <Typography variant="bodyW3">
            Employee profiles could not be loaded.{" "}
            <button type="button" onClick={() => void usersQuery.refetch()}>
              Try again
            </button>
          </Typography>
        )}
        <Link
          className="text-sm text-interactive-text underline"
          to={`/business-settings/${businessId}/users/technicians`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Add an employee
        </Link>
      </DrawerField>
      {teams.length > 0 && (
        <DrawerField label="Payroll teams">
          <MultiSelect
            label="Teams"
            options={teams}
            value={member.team_access_key_ids ?? []}
            disabled={saving}
            onChange={(ids) =>
              void setValues((current) => ({
                ...current,
                team_access_key_ids: ids,
                team_access_keys: rosterTeamSnapshots(
                  ids,
                  teams,
                  current.team_access_keys,
                ),
              }))
            }
          />
          <Typography variant="bodyW3" textColor="secondary">
            Each selected team can see this worker’s payroll content after
            publication. Unassigned workers are visible only to full-workbook
            users.
          </Typography>
        </DrawerField>
      )}
    </>
  );
};
